import { Injectable, inject, Injector, runInInjectionContext } from '@angular/core';
import {
  Firestore,
  collection,
  collectionData,
  doc,
  addDoc,
  updateDoc,
  deleteDoc
} from '@angular/fire/firestore';
import { Observable, from, map, combineLatest, of, catchError, shareReplay, take } from 'rxjs';
import { Functions, httpsCallable } from '@angular/fire/functions';
import Fuse from 'fuse.js';
import { FrequentPlace } from '../models/frequent-place.model';
import { DataConversionService } from './data-conversion.service';
import { LoadingService } from './loading.service';
@Injectable({
  providedIn: 'root'
})
export class FrequentPlacesService {
  private firestore: Firestore = inject(Firestore);
  private dataConversionService: DataConversionService = inject(DataConversionService);
  private loadingService: LoadingService = inject(LoadingService);
  private injector: Injector = inject(Injector);
  private functions: Functions = inject(Functions);

  private cachedWarehouses$: Observable<FrequentPlace[]> | null = null;

  /**
   * Get all frequent places for a specific user as a live stream.
   */
  getUserFrequentPlaces(userId: string): Observable<FrequentPlace[]> {
    return runInInjectionContext(this.injector, () => {
      const placesCollection = collection(this.firestore, `users/${userId}/frequentPlaces`);
      return collectionData(placesCollection, { idField: 'id' }).pipe(
        map(places => places.map(place => this.dataConversionService.fromFirestore(place) as FrequentPlace)),
        shareReplay({ bufferSize: 1, refCount: true })
      );
    });
  }

  /**
   * Get TCH warehouses from the 'warehouses' collection.
   * Caches the results so subsequent searches are instantaneous.
   */
  getWarehouses(): Observable<FrequentPlace[]> {
    if (this.cachedWarehouses$) {
      return this.cachedWarehouses$;
    }

    this.cachedWarehouses$ = runInInjectionContext(this.injector, () => {
      const warehousesCollection = collection(this.firestore, 'warehouses');
      
      return collectionData(warehousesCollection, { idField: 'id' }).pipe(
        map((warehouses: any[]) => warehouses.map((data: any) => {
          const address1 = data.location?.address1 || '';
          const cityObj = data.location?.city || '';
          const stateObj = data.location?.state || '';
          const zipObj = data.location?.zipcode || data.location?.zip || '';
          
          // Construct a highly readable string for display and searching
          const addressStr = [address1, cityObj, stateObj, zipObj].filter(p => !!p && p.trim() !== '').join(', ');
          
          return {
            id: data.id,
            nickname: data.name ? `TCH - ${data.name}` : 'TCH Warehouse',
            address: addressStr || 'No Address',
            placeId: '',
            isGC: false
          } as FrequentPlace;
        })),
        catchError(err => {
          console.error('Error fetching warehouses:', err);
          return of([]);
        }),
        shareReplay({ bufferSize: 1, refCount: true })
      );
    });

    return this.cachedWarehouses$;
  }

  /**
   * Search specifically for General Contractors using the Cloud Function.
   */
  searchAccountsRemotely(term: string): Observable<FrequentPlace[]> {
    if (!term || term.length < 2) return of([]);

    return runInInjectionContext(this.injector, () => {
      const searchFn = httpsCallable(this.functions, 'searchAccounts');
      return from(searchFn({ query: term, type: 'gc' })).pipe(
        map((res: any) => {
          const accounts = res.data.results as any[];
          return accounts.map(acc => {
            const street1 = acc.address?.street1 || '';
            const cityObj = acc.address?.city || '';
            const stateObj = acc.address?.state || '';
            const zipObj = acc.address?.zip || '';
            const addressStr = [street1, cityObj, stateObj, zipObj].filter(p => !!p && p.trim() !== '').join(', ');
            
            return {
              id: acc.id,
              nickname: acc.accountName || 'Unknown Account',
              address: addressStr || 'No Address',
              placeId: '',
              isGC: true
            } as FrequentPlace;
          });
        }),
        catchError(err => {
          console.error('Error in searchAccounts cloud function:', err);
          return of([]);
        })
      );
    });
  }

  /**
   * Save a frequent place for a user.
   */
  async saveFrequentPlace(userId: string, place: Partial<FrequentPlace>): Promise<string> {
    this.loadingService.show('Saving place...');
    try {
      const placeData = this.dataConversionService.toFirestore(place);
      const id = placeData.id;
      delete (placeData as any).id;
      
      return await runInInjectionContext(this.injector, async () => {
        if (id) {
          const placeDoc = doc(this.firestore, `users/${userId}/frequentPlaces/${id}`);
          await updateDoc(placeDoc, placeData as any);
          return id;
        } else {
          const placesCollection = collection(this.firestore, `users/${userId}/frequentPlaces`);
          const docRef = await addDoc(placesCollection, placeData as FrequentPlace);
          return docRef.id;
        }
      });
    } finally {
      this.loadingService.hide();
    }
  }

  /**
   * Delete a frequent place.
   */
  async deleteFrequentPlace(userId: string, placeId: string): Promise<void> {
    this.loadingService.show('Deleting place...');
    try {
      await runInInjectionContext(this.injector, () => {
        const placeDoc = doc(this.firestore, `users/${userId}/frequentPlaces/${placeId}`);
        return deleteDoc(placeDoc);
      });
    } finally {
      this.loadingService.hide();
    }
  }

  /**
   * Get the default office (warehouse) for a branch code.
   */
  getDefaultOffice(branchCode: string): Observable<FrequentPlace | null> {
    return this.getWarehouses().pipe(
      map(warehouses => {
        const warehouse = warehouses.find(w => w.id === branchCode || w.nickname.toLowerCase().includes(branchCode.toLowerCase()));
        if (warehouse) {
          return {
            ...warehouse,
            nickname: `Default Office (${warehouse.nickname.replace('TCH - ', '')})`
          };
        }
        return null;
      })
    );
  }

  /**
   * Search for places using Fuse.js fuzzy engine and Cloud Function.
   */
  searchPlaces(userId: string, term: string, defaultBranch?: string): Observable<FrequentPlace[]> {
    const places$ = combineLatest([
      this.getUserFrequentPlaces(userId).pipe(take(1)),
      this.getWarehouses().pipe(take(1)),
      term && term.length >= 2 ? this.searchAccountsRemotely(term).pipe(take(1)) : of([])
    ]);

    return places$.pipe(
      map(([userPlaces, warehouses, remoteAccounts]) => {
        const localPlaces: FrequentPlace[] = [...userPlaces, ...warehouses];
        
        if (!term || term.length < 2) {
          // If no search term, return all local places, but prioritize default office if it matches
          if (defaultBranch) {
             const defaultOffice = warehouses.find(w => w.id === defaultBranch);
             if (defaultOffice) {
               const otherPlaces = localPlaces.filter(p => p.id !== defaultOffice.id);
               return [
                 { ...defaultOffice, nickname: `Default Office (${defaultOffice.nickname.replace('TCH - ', '')})` },
                 ...otherPlaces
               ];
             }
          }
          return localPlaces;
        }
        
        // Initialize Fuse locally on the combined array
        const fuse = new Fuse(localPlaces, {
          keys: ['nickname', 'address'],
          threshold: 0.3, 
          ignoreLocation: true, 
          includeScore: true
        });

        let results = fuse.search(term).map(r => r.item);
        
        // Add remote accounts to the results
        results = [...results, ...remoteAccounts];

        // Ensure default office is always easily accessible if it matches the term or if term is empty
        if (defaultBranch) {
          const defaultOffice = warehouses.find(w => w.id === defaultBranch);
          if (defaultOffice && !results.some(r => r.id === defaultOffice.id)) {
            // Only add if it's a reasonably good match or we're just starting search
            if (defaultOffice.nickname.toLowerCase().includes(term.toLowerCase()) || 
                defaultOffice.address.toLowerCase().includes(term.toLowerCase())) {
              results.unshift({ ...defaultOffice, nickname: `Default Office (${defaultOffice.nickname.replace('TCH - ', '')})` });
            }
          }
        }

        return results;
      })
    );
  }
}
